import React, { useState, useRef, useEffect } from 'react';
import { validateSymbol } from '../api';
import SymbolSuggestion from './SymbolSuggestion';
import TickerAutocomplete from './TickerAutocomplete';

/**
 * Ticker lookup for the nav bar. On desktop it's a compact magnifier that
 * expands into an input on click (right cluster). On mobile it's rendered as a
 * full-width labeled search row pinned to the top of the opened menu — the
 * layout differences are handled by `.nav-search*` rules in App.css.
 *
 * On submit it validates the symbol (alias / asset-class / typo aware) so junk
 * lookups like "sandp500" surface a one-click "did you mean" instead of landing
 * on a dead /stock/ page.
 *
 * Parent supplies `onNavigate(path)` so it can also close the mobile menu.
 */
export default function NavTickerSearch({ onNavigate }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null); // resolve_symbol response when invalid
  const [checking, setChecking] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const go = (sym, at) => {
    const suffix = at && at !== 'auto' && at !== 'stock' ? `?asset_type=${at}` : '';
    onNavigate(`/stock/${sym}${suffix}`);
    setQuery('');
    setResult(null);
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const t = query.trim().toUpperCase();
    if (!t) return;
    setChecking(true);
    setResult(null);
    try {
      const { data } = await validateSymbol(t, 'auto');
      if (data?.valid) { go(data.symbol || t, data.asset_type); return; }
      setResult(data);
    } catch {
      go(t, 'auto'); // couldn't verify — navigate anyway rather than block
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="nav-search">
      <button
        type="button"
        className="nav-search-toggle"
        aria-label="Look up a stock or FX ticker"
        onClick={() => setOpen((o) => !o)}
      >
        🔍
      </button>
      <form className={`nav-search-form${open ? ' open' : ''}`} onSubmit={submit}>
        <TickerAutocomplete
          inputRef={inputRef}
          value={query}
          onChange={(val) => { setQuery(val); if (result) setResult(null); }}
          onPick={(sym, at) => go(sym, at)}
          placeholder={checking ? 'Checking…' : 'Look up ticker…'}
          aria-label="Look up a stock or FX ticker"
          wrapperStyle={{ width: '100%' }}
          menuMinWidth={240}
          menuAlign="right"
        />
      </form>
      {open && result && !result.valid && (
        <div className="nav-search-suggest">
          {result.error && (
            <div style={{ fontSize: 11, color: 'var(--red, #f85149)', marginBottom: 2 }}>{result.error}</div>
          )}
          <SymbolSuggestion result={result} onApply={go} compact />
        </div>
      )}
    </div>
  );
}
